import React from 'react';
import { useSelector } from 'react-redux';
import { Copy, Forms, Loading, SystemText, TitleBar } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';
import { apiGet } from 'services';
import WidgetBase, { groupSet, metricNameFromId, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import Correlation from '../components/correlation';
import { e4Format, e4Formats } from 'helpers/dates';

export const MetricsOverTime = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [flipped, setFlipped] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    const metrics = useSelector(state => state.dashboard.metrics);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const st = path => SystemText(`Dashboard/Widgets/MetricsOverTime/${path}`);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = { show_For: true, period: 'M' };

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = st('Fields/Group/Error');
            validForm = false;
        }

        if (!metricSet(form.metric1)) {
            errors.metric1 = st('Fields/Metric1/Errors/Mandatory');
            validForm = false;
        }

        if (!metricSet(form.metric2)) {
            errors.metric2 = st('Fields/Metric2/Errors/Mandatory');
            validForm = false;
        }

        if (form.metric1?.subMetricId && (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)) {
            errors.metric1 = st('Fields/Metric1/Errors/Options');
            validForm = false;
        }

        if (form.metric2?.subMetricId && (!form.metric2?.subMetricValues || form.metric2?.subMetricValues.length === 0)) {
            errors.metric2 = st('Fields/Metric2/Errors/Options');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div
            onClick={() => setFlipped(!flipped)}
            className={`chart-container ${flipped ? 'flipped' : ''}`}
            id={`chart-${item.id}`}
            style={{ height: `${height}px` }}
        >
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart front={flipped} data={[data[0]]} item={item} id={`chart-${item.id}`} />
            </div>
            {data.length > 1 && (
                <div className="chart" style={{ height: `${height}px` }}>
                    <Chart front={!flipped} data={[data[1]]} item={item} id={`chart-${item.id}`} axis="right" />
                </div>
            )}
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        if (!recommendationData) return null;

        const result = [
            <Correlation key={`recommendation-${item.id}`} widget={item} value={recommendationData.correlation} />,
        ];

        const data = JSON.parse(item.data);
        if (!recommendationData?.biggest1) return result;
        const same = recommendationData.biggest1?.x === recommendationData.biggest2?.x;
        const metric1 = values.legend2 || metricNameFromId(data.metric1, metrics);
        const metric2 = values.legend2 || metricNameFromId(data.metric2, metrics);
        const text = same
            ? SystemText(
                  'Dashboard/Widgets/MetricsOverTime/Recommends/Same',
                  metric1,
                  metric2,
                  e4Format(recommendationData.biggest1?.x, e4Formats.monthLong)
              )
            : `${SystemText(
                  'Dashboard/Widgets/MetricsOverTime/Recommends/Different/First',
                  metric1,
                  e4Format(recommendationData.biggest1?.x, e4Formats.monthLong)
              )}${SystemText(
                  'Dashboard/Widgets/MetricsOverTime/Recommends/Different/Second',
                  metric2,
                  e4Format(recommendationData.biggest2?.x, e4Formats.monthLong)
              )}`;

        result.push(
            <div>
                <p>
                    <Copy value={text} />
                </p>
            </div>
        );

        return result;
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <MetricPicker
                label={st('Fields/Metric1/Label')}
                mandatory
                value={values.metric1}
                error={errors.metric1}
                onChange={value => updateProperty(value, 'metric1')}
                showAverage
            />
            <MetricPicker
                label={st('Fields/Metric2/Label')}
                mandatory
                value={values.metric2}
                error={errors.metric2}
                onChange={value => updateProperty(value, 'metric2')}
                showAverage
            />
            <TitleBar
                title={SystemText('Admin/General/More')}
                data-testid="span-show-more"
                openTitle={SystemText('Admin/General/Less')}
                className="showMore"
            >
                <Forms.Text
                    label={st('Fields/Legend1/Label')}
                    help={st('Fields/Legend1/Help')}
                    placeholder={st('Fields/Legend1/Placeholder')}
                    maxLength="50"
                    value={values.legend1}
                    onChange={value => updateProperty(value, 'legend1')}
                />
                <Forms.Text
                    label={st('Fields/Legend2/Label')}
                    help={st('Fields/Legend2/Help')}
                    placeholder={st('Fields/Legend2/Placeholder')}
                    maxLength="50"
                    value={values.legend2}
                    onChange={value => updateProperty(value, 'legend2')}
                />
            </TitleBar>
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default MetricsOverTime;
